/**
 * Centralized navigation targets. `App.tsx`'s `<Route path>` patterns stay as
 * literal strings (they're declared once, matched against by the router, and
 * carry `:param` placeholders rather than concrete values) — this module is
 * for the concrete URLs built at `navigate()`/`<Link to>` call sites, which
 * used to duplicate the same template string across a dozen files.
 */
export const ROUTES = {
  login: '/login',
  forgotPassword: '/forgot-password',
  home: '/',
  admin: '/admin',
  adminView: (tab: string, workspaceId?: string) => {
    const params = new URLSearchParams({ tab })
    if (workspaceId) params.set('workspace_id', workspaceId)
    return `/admin?${params}`
  },
  settings: '/settings',
  settingsBilling: '/settings?tab=billing',
  workspaceNew: '/workspaces/new',
  topic: (topicId: string) => `/topics/${topicId}`,
  topicThread: (topicId: string, threadId: string) =>
    `/topics/${topicId}?thread=${threadId}`,
  topicSettings: (topicId: string) => `/topics/${topicId}/settings`,
  topicResources: (topicId: string) => `/topics/${topicId}/resources`,
  topicNew: (workspaceId: string) => `/workspaces/${workspaceId}/topics/new`,
  workspaceSettings: (workspaceId: string) =>
    `/workspaces/${workspaceId}/settings`,
  workspaceSettingsTab: (workspaceId: string, tab: string) =>
    `/workspaces/${workspaceId}/settings?tab=${tab}`,
  workspaceResources: (workspaceId: string) =>
    `/workspaces/${workspaceId}/resources`,
  /** Unified resource detail (redesign §5) — every reference links here. */
  resourceDetail: (resourceId: string) => `/r/${resourceId}`,
  waiting: (workspaceId: string) => `/workspaces/${workspaceId}/waiting`,
  resourceNew: (workspaceId: string) =>
    `/workspaces/${workspaceId}/resources/new`,
  topicResourceNew: (topicId: string) => `/topics/${topicId}/resources/new`,
  resourceEdit: (workspaceId: string, resourceId: string) =>
    `/workspaces/${workspaceId}/resources/${resourceId}/edit`,
  topicResourceEdit: (topicId: string, resourceId: string) =>
    `/topics/${topicId}/resources/${resourceId}/edit`,
  workspaceSkills: (workspaceId: string) => `/workspaces/${workspaceId}/skills`,
  workspaceRuns: (workspaceId: string) => `/workspaces/${workspaceId}/runs`,
  workspaceRecipes: (workspaceId: string) =>
    `/workspaces/${workspaceId}/recipes`,
  /** The Recipes page with a recipe's setup open (on a source, if given). */
  recipeSetup: (workspaceId: string, recipeId: string, sourceId?: string) => {
    const params = new URLSearchParams({ setup: recipeId })
    if (sourceId) params.set('source', sourceId)
    return `/workspaces/${workspaceId}/recipes?${params}`
  },
  /** The Recipes page with an install's setup open. */
  recipeInstall: (workspaceId: string, installId: string) =>
    `/workspaces/${workspaceId}/recipes?install=${encodeURIComponent(installId)}`,
  runDetail: (workspaceId: string, runId: string) =>
    `/workspaces/${workspaceId}/runs/${runId}`,
  agentNew: (workspaceId: string) => `/workspaces/${workspaceId}/agents/new`,
  agentNewPrompt: (workspaceId: string) =>
    `/workspaces/${workspaceId}/agents/new/prompt`,
  agentProfile: (workspaceId: string, agentId: string) =>
    `/workspaces/${workspaceId}/agents/${agentId}`,
  personProfile: (workspaceId: string, username: string) =>
    `/workspaces/${workspaceId}/people/${encodeURIComponent(username)}`,
  agentEdit: (workspaceId: string, agentId: string) =>
    `/workspaces/${workspaceId}/agents/${agentId}/edit`,
  agentEditPrompt: (workspaceId: string, agentId: string) =>
    `/workspaces/${workspaceId}/agents/${agentId}/edit/prompt`,
} as const

/** True when `pathname` is `base` or one of its sub-routes. */
export function isRouteActive(pathname: string, base: string): boolean {
  return pathname === base || pathname.startsWith(`${base}/`)
}
